import React from "react";
import { Typography, Button, Grid2 as Grid } from "@mui/material";
import { ParticipantData } from "../../models/Participant";
import { Registration, RegistrationStatus } from "../../models/Registration";
import Course from "../../models/Course";

import dayjs from "dayjs";
import registrationService from "../../services/registrationService";
import useNotification from "../../hooks/useNotification";
import useConfirmDialog from "../../hooks/useConfirmationDialog";
import { formatDate } from "../../shared/helperFunctions";

interface ParticipantRendererProps {
  participant: ParticipantData;
  registration: Registration | null;
  course: Course;
  onReturn: (updateCourseRoster: boolean) => void;
}

const ParticipantRenderer: React.FC<ParticipantRendererProps> = ({
  participant,
  registration,
  course,
  onReturn,
}) => {
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  // const formatDate = (date: Date) => {
  //   return dayjs(date).format("MMM D, YYYY h:mm A");
  // };
  // const confirmDialog = useConfirmDialog();
  const { showNotification } = useNotification();

  async function handleDeleteRegistration(): Promise<void> {
    const confirmation = await confirmDialog({
      title: "Confirm Delete Registration",
      content: `Are you sure you want to delete the registration of ${participant.firstName} ${participant.lastName}? If the participant is only registered for this course, the participant will be removed from the system.`,
      isDangerous: true,
    });

    if (!confirmation) {
      return;
    }

    //delete registration
    try {
      if (registration) {
        await registrationService.deleteRegistration(
          course.id,
          registration.id
        );
        //refresh course roster
        onReturn(true);
      }
    } catch (error) {
      showNotification("Failed to delete registration", "error");
    }
  }

  return (
    <div>
      <Grid container spacing={2}>
        <Grid container spacing={1} size={{ xs: 12 }}>
          <Grid size={{ xs: 4 }}>
            <span style={{ color: "blue", fontWeight: "bold" }}>
              Name: &nbsp;
            </span>
          </Grid>
          <Grid size={{ xs: 8 }}>
            {participant.firstName} {participant.lastName}
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}></Grid>
        <Grid container spacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 4 }}>
            <span style={{ color: "blue", fontWeight: "bold" }}>
              Position: &nbsp;
            </span>
          </Grid>
          <Grid size={{ xs: 8 }}>{participant.positionTitle}</Grid>
        </Grid>
        <Grid container spacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 4 }}>
            <span style={{ color: "blue", fontWeight: "bold" }}>
              Organization: &nbsp;
            </span>
          </Grid>
          <Grid size={{ xs: 8 }}>{participant.organization}</Grid>
        </Grid>
        <Grid container spacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 4 }}>
            <span style={{ color: "blue", fontWeight: "bold" }}>
              Address: &nbsp;
            </span>
          </Grid>
          <Grid size={{ xs: 8 }}>{participant.address}</Grid>
        </Grid>
        <Grid size={{ xs: 12 }}></Grid>
        <Grid container spacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 4 }}>
            <span style={{ color: "blue", fontWeight: "bold" }}>
              Phone: &nbsp;
            </span>
          </Grid>
          <Grid size={{ xs: 8 }}>{participant.phoneNumber}</Grid>
        </Grid>
        <Grid container spacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 4 }}>
            <span style={{ color: "blue", fontWeight: "bold" }}>
              Email Address: &nbsp;
            </span>
          </Grid>
          <Grid size={{ xs: 8 }}>{participant.email}</Grid>
        </Grid>
        <Grid size={{ xs: 12 }}></Grid>
        <Grid container spacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 4 }}>
            <span style={{ color: "blue", fontWeight: "bold" }}>
              Course Registered: &nbsp;
            </span>
          </Grid>
          <Grid size={{ xs: 8 }}>{course.name}</Grid>
        </Grid>
        <Grid container spacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 4 }}>
            <span style={{ color: "blue", fontWeight: "bold" }}>
              Registration Date: &nbsp;
            </span>
          </Grid>
          <Grid size={{ xs: 8 }}>
            {registration?.registrationDate
              ? formatDate(registration.registrationDate)
              : "N/A"}
          </Grid>
        </Grid>
        <Grid container spacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 4 }}>
            <span style={{ color: "blue", fontWeight: "bold" }}>
              Registration Status: &nbsp;
            </span>
          </Grid>
          <Grid size={{ xs: 8 }}>
            {registration?.status
              ? registration.status.charAt(0).toUpperCase() +
                registration.status.slice(1)
              : "N/A"}
          </Grid>
        </Grid>
        {registration?.status === RegistrationStatus.APPROVED && (
          <Grid container spacing={2} size={{ xs: 12 }}>
            <Grid size={{ xs: 4 }}>
              <span style={{ color: "blue", fontWeight: "bold" }}>
                Approved By: &nbsp;
              </span>
            </Grid>
            {/*build a string for first and last name, if blank use email*/}
            <Grid size={{ xs: 8 }}>
              {(() => {
                const first = registration?.approvedBy?.firstName?.trim();
                const last = registration?.approvedBy?.lastName?.trim();
                const email = registration?.approvedBy?.email;
                if (first || last) {
                  return `${first || ""}${first && last ? " " : ""}${
                    last || ""
                  }`.trim();
                } else if (email) {
                  return email;
                } else {
                  return "N/A";
                }
              })()}
            </Grid>
          </Grid>
        )}
        {registration?.status === RegistrationStatus.APPROVED && (
          <Grid container spacing={2} size={{ xs: 12 }}>
            <Grid size={{ xs: 4 }}>
              <span style={{ color: "blue", fontWeight: "bold" }}>
                Approved Date: &nbsp;
              </span>
            </Grid>
            <Grid size={{ xs: 8 }}>
              {registration?.approvalDate
                ? formatDate(registration.approvalDate)
                : "N/A"}
            </Grid>
          </Grid>
        )}
        <Grid size={{ xs: 12 }}></Grid>
        <Grid container spacing={2} size={{ xs: 12 }}>
          <Grid size={{ xs: 3 }}>
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={() => console.log("TODO")}
            >
              Approve
            </Button>
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={() => console.log("TODO")}
            >
              Deny
            </Button>
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => handleDeleteRegistration()}
            >
              Delete
            </Button>
          </Grid>
          <Grid size={{ xs: 3 }}>
            <Button
              variant="contained"
              color="info"
              onClick={() => onReturn(false)}
            >
              Return
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default ParticipantRenderer;
